import React, { useEffect, useMemo, useRef } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { useContainerSize } from '../hooks/useContainerSize';
import type { FlatRow } from '../model/flatten-tree';
import { getTypeTagStyle } from '../styles/theme';
import type { SafeJsonEditorTheme } from '../types';
import { CaretDownIcon, CaretRightIcon } from './icons';

export interface VirtualizedTreeProps {
  rows: FlatRow[];
  collapsedPaths: Set<string>;
  onToggleCollapse: (rowKey: string) => void;
  selectedRowKey: string | null;
  onSelectRow: (rowKey: string) => void;
  theme: SafeJsonEditorTheme;
  rowHeight?: number;
  scrollToRowKey?: string | null;
}

const ROW_HEIGHT_DEFAULT = 30;

/**
 * Renders the flattened row list via react-window's FixedSizeList so only
 * the rows currently in the viewport are mounted as DOM, regardless of how
 * many thousands of nodes the underlying JSON has.
 */
export const VirtualizedTree: React.FC<VirtualizedTreeProps> = ({
  rows,
  collapsedPaths,
  onToggleCollapse,
  selectedRowKey,
  onSelectRow,
  theme,
  rowHeight = ROW_HEIGHT_DEFAULT,
  scrollToRowKey,
}) => {
  const { ref, size } = useContainerSize<HTMLDivElement>();
  const listRef = useRef<FixedSizeList>(null);

  useEffect(() => {
    if (!scrollToRowKey) return;
    const index = rows.findIndex((row) => row.rowKey === scrollToRowKey);
    if (index >= 0) listRef.current?.scrollToItem(index, 'center');
  }, [scrollToRowKey, rows]);

  const Row = useMemo(() => {
    const RowRenderer: React.FC<ListChildComponentProps> = ({ index, style }) => {
      const row = rows[index];
      const isFoldable = row.nodeType === 'object' || row.nodeType === 'array';
      const isCollapsed = collapsedPaths.has(row.rowKey);
      const isSelected = row.rowKey === selectedRowKey;

      return (
        <div
          style={{ ...style, paddingLeft: 8 + Math.min(row.depth * 16, 160) }}
          className={['sje-row', isSelected ? 'sje-row-selected' : '', row.isMatched ? 'sje-row-matched' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onSelectRow(row.rowKey)}
          role="treeitem"
          aria-selected={isSelected}
        >
          {isFoldable ? (
            <button
              type="button"
              className="sje-row-caret"
              onClick={(event) => {
                event.stopPropagation();
                onToggleCollapse(row.rowKey);
              }}
              aria-label={isCollapsed ? `Expand ${row.keyLabel}` : `Collapse ${row.keyLabel}`}
            >
              {isCollapsed ? <CaretRightIcon /> : <CaretDownIcon />}
            </button>
          ) : (
            <span className="sje-row-caret-placeholder" />
          )}
          <span className={`sje-row-key${row.isRoot ? ' sje-row-key-root' : ''}`}>{row.keyLabel}</span>
          <span className="sje-row-type-tag" style={getTypeTagStyle(theme, row.nodeType)}>
            {row.nodeType}
          </span>
          <span className="sje-row-preview">
            {row.nodeType === 'object'
              ? `${row.childCount ?? 0} keys`
              : row.nodeType === 'array'
                ? `${row.childCount ?? 0} items`
                : ''}
          </span>
        </div>
      );
    };
    return RowRenderer;
  }, [rows, collapsedPaths, selectedRowKey, onSelectRow, onToggleCollapse, theme]);

  return (
    <div className="sje-tree-scroll" role="tree" ref={ref}>
      {size.height > 0 && size.width > 0 ? (
        <FixedSizeList ref={listRef} height={size.height} width={size.width} itemCount={rows.length} itemSize={rowHeight}>
          {Row}
        </FixedSizeList>
      ) : null}
    </div>
  );
};
